import {
  SessionInputs,
  WaveInputs,
  WaveResult,
  SkillResult,
  Hazard,
  GamePlayData,
  CurrencyEnum,
  SkillStates,
  RideResult,
  SkillEnum,
  SkillResults,
  HazardSeverity
} from '../types'
import { SKILL_ORDER } from '../constants/skillOrder'
import SKILLS from '@/constants/skills'

const HAZARD_PROBABILITIES = {
  small: { injury: 0.005, damage: 0.005 },
  medium: { injury: 0.002, damage: 0.002 },
  big: { injury: 0.0005, damage: 0.0005 }
}

const AVERAGE_MOVE_TIME = 0.8

function generateSkillPoints(
  overallSkillMultiplier: number,
  skillMultiplier: number,
  critProbability: number,
  critMultiplier: number
): SkillResult {
  const basePoints = Math.random() * 10
  const isCritical = basePoints > 10 - critProbability * 10
  const points = basePoints * overallSkillMultiplier * skillMultiplier * (isCritical ? critMultiplier : 1)
  const respect = isCritical ? 1 + Math.random() * 0.5 * critMultiplier : 0
  return { basePoints, points, respect, isCritical }
}

export function generateHazards(waveHeight: string, fitnessSkillLevel: number): Hazard[] {
  const hazards: Hazard[] = []
  const waveHeightMultiplier =
    {
      knee_high: 0.6,
      waist_high: 0.8,
      shoulder_high: 1,
      head_high: 1.5,
      overhead: 2,
      one_and_half_overhead: 2.5,
      two_overhead: 3,
      three_overhead: 4,
      big_wave: 5
    }[waveHeight] || 1

  // Fitness level reduces injury probability (1.0 to 0.2 multiplier)
  const fitnessMultiplier = 1 - (fitnessSkillLevel - 1) * 0.2

  // Check for injury first
  const baseInjuryProbability = 0.01 * waveHeightMultiplier * fitnessMultiplier
  const injuryRoll = Math.random()

  if (injuryRoll < baseInjuryProbability) {
    // Determine severity based on wave height
    let severity: HazardSeverity
    const severityRoll = Math.random()

    if (waveHeightMultiplier <= 0.8) {
      // knee_high and waist_high
      severity = severityRoll < 0.7 ? 'small' : severityRoll < 0.9 ? 'medium' : 'big'
    } else if (waveHeightMultiplier >= 2) {
      // overhead and bigger
      severity = severityRoll < 0.5 ? 'big' : severityRoll < 0.8 ? 'medium' : 'small'
    } else {
      // middle range waves
      severity = severityRoll < 0.3 ? 'big' : severityRoll < 0.7 ? 'medium' : 'small'
    }

    const injuryCosts = {
      small: 5,
      medium: 25,
      big: 60
    }

    hazards.push({
      type: 'injury',
      severity,
      consequenceCost: injuryCosts[severity],
      consequenceCurrency: 'energy'
    })
  }

  // Check for damage separately
  const baseDamageProbability = 0.005 * waveHeightMultiplier
  const damageRoll = Math.random()

  if (damageRoll < baseDamageProbability) {
    let severity: HazardSeverity
    const severityRoll = Math.random()

    if (waveHeightMultiplier <= 0.8) {
      // knee_high and waist_high
      severity = severityRoll < 0.7 ? 'small' : severityRoll < 0.1 ? 'medium' : 'big'
    } else if (waveHeightMultiplier >= 2) {
      // overhead and bigger
      severity = severityRoll < 0.5 ? 'big' : severityRoll < 0.8 ? 'medium' : 'small'
    } else {
      // middle range waves
      severity = severityRoll < 0.3 ? 'big' : severityRoll < 0.7 ? 'medium' : 'small'
    }

    const damageCosts = {
      small: 50,
      medium: 150,
      big: 500
    }

    hazards.push({
      type: 'damage',
      severity,
      consequenceCost: damageCosts[severity],
      consequenceCurrency: 'money'
    })
  }

  return hazards
}

export function generatePlayerWaveResult(
  gameData: GamePlayData,
  sessionInputs: SessionInputs,
  waveInputs: WaveInputs
): WaveResult {
  const catchProbability = waveInputs.catchProbability || 1 // Default to 1 if not provided
  const randomValue = Math.random()
  const rideResult: RideResult =
    randomValue < catchProbability ? 'ride' : randomValue < catchProbability + 0.2 ? 'wipeout' : 'no_ride'

  const skills: SkillResults = {}
  let hazards: Hazard[] = []

  if (rideResult === 'ride' || rideResult === 'wipeout') {
    hazards = generateHazards(sessionInputs.waveHeight, gameData.skills.fitness.level)
  }

  if (rideResult === 'ride') {
    let remainingMoves = Math.floor(waveInputs.waveTime / AVERAGE_MOVE_TIME)
    const startSkill =
      gameData.skills.green_wave_riding?.level > 0 && gameData.isSurfingGreenWave
        ? 'green_wave_riding'
        : 'whitewater_riding'

    let currentSkill = SKILLS[startSkill]
    let maneuverCount = 0

    // Continue while we have a valid skill and either:
    // - it's a knowledge/base skill (no move limit)
    // - or we haven't reached max moves for maneuvers
    while (currentSkill && (currentSkill.type !== 'maneuver' || maneuverCount < remainingMoves)) {
      // Generate points for current skill if player has it unlocked
      const skillState = gameData.skills[currentSkill.key]
      if (skillState.level > 0) {
        skills[currentSkill.key] = generateSkillPoints(
          gameData.currencies.skill.totalMultiplier || 1,
          skillState.totalMultiplier || 1,
          gameData.currencies.crit_probability.amount *
            (gameData.currencies.crit_probability.totalMultiplier || 1) *
            gameData.surfSession.critProbabilityComboMultiplier,
          gameData.currencies.crit_multiplier.amount * (gameData.currencies.crit_multiplier.totalMultiplier || 1)
        )

        // Count maneuver usage
        if (currentSkill.type === 'maneuver') {
          maneuverCount++
        }
      }

      // Get next possible skills
      const nextPossibleSkills = SKILL_ORDER.progression[currentSkill.key]?.next || []

      // Filter to only unlocked skills
      const availableSkills = nextPossibleSkills.filter(
        (skill) =>
          gameData.skills[skill].level > 0 && (startSkill !== 'whitewater_riding' || SKILLS[skill].type !== 'maneuver')
      )

      // If no available next skills, end the ride
      if (availableSkills.length === 0) {
        break
      }

      // Select next skill randomly from available options
      currentSkill = SKILLS[availableSkills[Math.floor(Math.random() * availableSkills.length)]]
    }
  }

  return {
    sessionInputs,
    waveInputs,
    skills,
    hazards,
    rideResult
  }
}
